import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useResendTimer } from "@/hooks/useResendTimer";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";

interface OTPStepProps {
  maskedNumber: string;
  otpLength: number;
  onVerify: (otp: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  remainingAttempts?: number;
}

export default function OTPStep({
  maskedNumber,
  otpLength,
  onVerify,
  onResend,
  onBack,
  loading,
  error,
  remainingAttempts,
}: OTPStepProps) {
  const { language } = useLanguage();
  const isAr = language === "ar";
  const [otp, setOtp] = useState("");
  const { countdown, canResend, maxResendsReached, triggerResend } = useResendTimer();
  const [shake, setShake] = useState(false);

  // Auto-submit on complete
  useEffect(() => {
    if (otp.length === otpLength && !loading) {
      onVerify(otp);
    }
  }, [otp, otpLength]);

  // Shake on error
  useEffect(() => {
    if (error) {
      setShake(true);
      setOtp("");
      const t = setTimeout(() => setShake(false), 600);
      return () => clearTimeout(t);
    }
  }, [error]);

  const handleResend = async () => {
    if (!canResend) return;
    triggerResend();
    await onResend();
  };

  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <BackArrow className="h-4 w-4" />
        {isAr ? "تغيير رقم الهاتف" : "Change phone number"}
      </button>

      {/* Title */}
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold text-foreground">
          {isAr ? "أدخل رمز التحقق" : "Enter verification code"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isAr ? `تم إرسال الرمز إلى ${maskedNumber}` : `Code sent to ${maskedNumber}`}
        </p>
      </div>

      {/* OTP Input */}
      <div className={`flex justify-center ${shake ? "animate-shake" : ""}`} dir="ltr">
        <InputOTP
          maxLength={otpLength}
          value={otp}
          onChange={setOtp}
          disabled={loading}
        >
          <InputOTPGroup>
            {Array.from({ length: otpLength }).map((_, i) => (
              <InputOTPSlot
                key={i}
                index={i}
                className={`w-12 h-14 text-xl font-bold ${
                  error ? "border-destructive" : "border-input"
                }`}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-center text-destructive">
          {error}
          {remainingAttempts !== undefined && (
            <span className="block mt-1">
              {isAr
                ? `المحاولات المتبقية: ${remainingAttempts}`
                : `${remainingAttempts} attempts remaining`}
            </span>
          )}
        </p>
      )}

      {/* Resend */}
      <div className="text-center text-sm">
        {maxResendsReached ? (
          <span className="text-muted-foreground">
            {isAr ? "حاول مرة أخرى لاحقاً" : "Please try again later"}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {isAr ? "لم يصلك الرمز؟ " : "Didn't receive it? "}
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                className="text-primary font-medium hover:underline"
              >
                {isAr ? "إعادة الإرسال" : "Resend"}
              </button>
            ) : (
              <span>
                {isAr
                  ? `إعادة الإرسال (${countdown} ثانية)`
                  : `Resend (${countdown}s)`}
              </span>
            )}
          </span>
        )}
      </div>

      {/* Verify button */}
      <Button
        className="w-full"
        disabled={otp.length < otpLength || loading}
        onClick={() => onVerify(otp)}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          isAr ? "تحقق" : "Verify"
        )}
      </Button>
    </div>
  );
}
